<template>
	<div>
		<j-card class="bg5">
			<!-- Gross Premium Amount -->
			<div>
				<j-list :list="computedGrossPremium" class="bb1 bc7">
					<div class="flex xs-12 p2" slot="list" slot-scope="{ item }">
						<div class="flex xs-6 xs-left xs-middle fw7 c1 lh125">
							{{ item.label }}
							<j-button
								v-if="showButtons"
								size="xsmall"
								class="ml2"
								type="fifth"
								@click="updateGrossPremium()"
								>{{ $t("edit_gross_premium") }}</j-button
							>
						</div>
						<div class="flex xs-6 xs-right xs-middle lh125">
							{{ item.value }}
						</div>
					</div>
				</j-list>
			</div>

			<!-- Premium Loadings -->
			<div class="xs-12 p3 bb1 bc7">
				<div class="xs-12 fs3 lh125 fw7 c1">{{ $t("premium_loadings") }}</div>
				<j-list
					class="b1 bc7 mt2"
					:list="computedLoadings"
					v-if="computedLoadings.length"
				>
					<div class="flex xs-12 p3" slot="list" slot-scope="{ item }">
						<div class="flex xs-6 xs-left xs-middle fw7 c6 lh125">
							{{ item.label }}
						</div>
						<div class="flex xs-6 xs-right xs-middle lh125">
							<div class="mr3 xs-right ta3">
								<div class="lh125">{{ item.value }}</div>
								<div class="c6 lh125">{{ item.summary }}</div>
							</div>
							<div v-if="showButtons">
								<crm-icon
									name="delete"
									class="cu2"
									color="cError"
									size="small"
									@click.native="$emit('loading:remove', item)"
								/>
							</div>
						</div>
					</div>
				</j-list>
				<!-- Add button and loading state -->
				<div class="flex xs-12 xs-between" v-if="showButtons">
					<j-button
						v-can:or="['writeQuotes', 'writePolicies']"
						size="xsmall"
						class="mt2"
						@click="addLoading"
						>{{ $t("add_loading") }}</j-button
					>
				</div>
			</div>

			<!-- Premium Discounts -->
			<div>
				<!-- Discounts and Taxes -->
				<div class="xs-12 p3 bb1 bc7">
					<div class="xs-12 fs3 lh125 fw7 c1">{{ $t("discount_codes") }}</div>
					<j-list
						class="b1 bc7 mt2"
						:list="computedDiscounts"
						v-if="computedDiscounts.length > 0"
					>
						<div class="flex xs-12 p3" slot="list" slot-scope="{ item }">
							<div class="flex wrap xs-6 xs-left xs-middle">
								<div class="xs-12 fw7 c6 lh125">{{ item.label }}</div>
								<div class="xs12 c6 lh125">
									{{ $t("order") }}: {{ item.order }}
								</div>
							</div>
							<div class="flex xs-6 wrap xs-right xs-middle lh125">
								<div class="mr3 xs-right ta3">
									<div class="lh125">{{ item.value }}</div>
									<div class="c6 lh125">{{ item.summary }}</div>
								</div>
								<crm-icon
									v-if="showButtons"
									class="cu2 remove-discount"
									name="delete"
									color="cError"
									size="small"
									@click.native="$emit('discount:remove', item)"
								></crm-icon>
							</div>
						</div>
					</j-list>
					<div class="flex xs-12 xs-between">
						<j-button
							v-if="showButtons"
							class="mt2"
							size="xsmall"
							@click="addDiscount"
							>{{ $t("add_discount") }}</j-button
						>
					</div>
				</div>
			</div>

			<!-- Price Breakdown -->
			<div class="xs-12 p3 bb1 bc7" v-if="computedPriceBreakdown.length">
				<div class="xs-12 fs3 lh125 fw7 pb2">{{ $t("price_breakdown") }}</div>
				<div>
					<j-list :list="computedPriceBreakdown" :itemClasses="'mb1 bg4 r2'">
						<div class="flex xs-12 p3" slot="list" slot-scope="{ item }">
							<div class="flex wrap xs-6 xs-left xs-middle">
								<div class="xs-12 fw7 c6 lh125">{{ item.label }}</div>
								<div class="xs-12 c6 lh125">
									{{ $t("order") }}: {{ item.order }}
								</div>
							</div>
							<div class="xs-6 ta3">
								<div class="lh125">{{ item.value }}</div>
								<div class="c6 lh125">{{ item.summary }}</div>
							</div>
						</div>
					</j-list>
				</div>
			</div>

			<!-- Net Premium Amount -->
			<div>
				<j-list :list="computedNetPremium">
					<div
						class="flex xs-12 p3 bg7--l4 bc7 bt1"
						slot="list"
						slot-scope="{ item }"
					>
						<div class="flex xs-6 xs-left xs-middle fw7 c1 lh125">
							{{ item.label }}
						</div>
						<div class="flex xs-6 xs-right xs-middle lh125 fw7">
							{{ item.value }}
						</div>
					</div>
				</j-list>
			</div>

			<!-- payment frequency -->
			<div class="flex xs-12 p3 bg7--l4 bc7 bt1 fs3">
				<div class="flex xs-6 xs-left xs-middle fw7 c1 lh125">
					{{ $t("payment_frequency") }}
				</div>
				<div class="flex xs-6 xs-right xs-middle lh125 fw7">
					{{ computedPaymentFrequency }}
				</div>
			</div>
		</j-card>
	</div>
</template>

<script>
import JCard from "@/components/JCard"
import JList from "@/components/JList"
import JButton from "@/components/JButton"
import CrmIcon from "@/components/CrmIcon"

export default {
	name: "PremiumInfo",
	components: {
		JCard,
		JList,
		JButton,
		CrmIcon
	},
	props: {
		pricing: Object,
		productId: Object,
		showButtons: {
			type: Boolean,
			required: false,
			default: true
		}
	},
	methods: {
		addLoading() {
			let value = {
				productId: this.productId
			}
			this.$emit("loadings:open_modal", {
				label: this.$t("loadings"),
				value,
				component: "AddLoadings",
				type: "add"
			})
		},
		addDiscount() {
			let value = {
				order: this.pricing?.appliedDiscounts?.length || 0,
				calculationJsonLogic: undefined
			}
			this.$emit("discount:open_modal", {
				label: this.$t("discount"),
				value,
				component: "AddUpdateDiscount",
				type: "add"
			})
		},
		updateDiscount() {
			let [{ order, calculationLogic, id } = {}] =
				this.pricing?.appliedDiscounts || []
			let value = {
				order,
				calculationJsonLogic: calculationLogic,
				discountId: id
			}
			console.log({ value })
			this.$emit("discount:open_modal", {
				label: "Discount",
				value,
				component: "AddUpdateDiscount"
			})
		},
		updateGrossPremium() {
			const grossAmount = this.pricing?.originalPrice
			const currencyCode = this.pricing?.currencyCode
			let value = {
				grossAmount,
				currencyCode
			}
			this.$emit("gross_premium:open_modal", {
				label: `Gross Premium ${
					this.pricing?.currencyCode
						? "(" + this.pricing?.currencyCode + ")"
						: ""
				}`,
				value,
				component: "AdjustGrossPremium"
			})
		}
	},
	computed: {
		computedGrossPremium() {
			return [
				{
					label: this.$t("gross_premium"),
					value: this.pricing?.formattedOriginalPrice || 0
				}
			]
		},
		computedLoadings() {
			return (this.pricing?.loadings || []).map(loading => {
				const {
					id,
					codeName: { code = "", name = "" } = {},
					ratio = "0",
					flat
				} = loading
				const { formattedOriginalPrice, currencyCode = "" } = this.pricing

				return {
					id,
					label: `${name || code || "-"}`,
					value: `${currencyCode}${flat}`,
					summary: `(${parseFloat(
						(ratio * 100).toFixed(2)
					)}% of ${formattedOriginalPrice})`
				}
			})
		},
		computedDiscounts() {
			return (this.pricing?.appliedDiscounts || []).map(item => {
				const {
					id,
					formattedFlat: value,
					ratio = 0,
					formattedOriginalPrice,
					code,
					name,
					order
				} = item
				return {
					id,
					code,
					label: name || code || "-",
					value,
					summary: `(${(ratio * 100).toFixed(
						2
					)}% of ${formattedOriginalPrice})`,
					order
				}
			})
		},
		computedPriceBreakdown() {
			const loadingBreakdown = (this.pricing?.loadings || []).map(loading => {
				const {
					// eslint-disable-next-line
					codeName: { code, name } = { code: "", name: "" },
					flat = "0",
					ratio = "0",
					order
				} = loading
				const { currencyCode = "", formattedOriginalPrice } = this.pricing

				return {
					label: `${name}`,
					value: `${currencyCode}${flat.toFixed(2)}`,
					summary: `(${(ratio * 100).toFixed(
						2
					)}% of ${formattedOriginalPrice})`,
					order
				}
			})
			const taxesAndDiscounts = [
				...(this.pricing?.appliedDiscounts || []),
				...(this.pricing?.appliedTaxes || [])
			]
				.sort((a, b) => a.order - b.order)
				.map(item => {
					const {
						ratio = "0",
						formattedFlat = "0",
						name,
						code,
						order,
						formattedOriginalPrice
					} = item

					return {
						label: name || code || "-",
						value: formattedFlat,
						summary: `${(ratio * 100).toFixed(
							2
						)}% of ${formattedOriginalPrice}`,
						order
					}
				})
			return [...loadingBreakdown, ...taxesAndDiscounts]
		},
		computedNetPremium() {
			return [
				{
					label: this.$t("net_premium"),
					value: this.pricing?.formattedPrice
				}
			]
		},
		computedPaymentFrequency() {
			return this.pricing?.paymentFrequency
		}
	}
}
</script>

<style></style>
